/**
 * Marks the CURRENT document as being replaced by a page REFRESH rather than by the app
 * closing and opening again.
 *
 * `window.location.reload()` is not an app close/open: the process stays alive and the
 * session simply continues. Without this marker the tracker saw a reload as both —
 * `pagehide` reported a close at "now" and the reloaded document reported a start at
 * "now" — so every pull-to-refresh stamped a bogus start (and close) on an app that
 * never closed. See `frontend/src/helpers/deviceTracking.ts`.
 *
 * Lives in `sessionStorage` rather than `localStorage` on purpose:
 *   - it MUST survive the reload (same browsing context) so the next document can read
 *     it, and it must NOT survive the tab / WebView going away, so a marker can never
 *     leak into a genuinely fresh launch.
 *
 * Set it as the LAST statement before calling `window.location.reload()`.
 */

/** sessionStorage key holding the epoch millis of the refresh. */
const PAGE_REFRESH_KEY = "app_page_refreshed_at";

/**
 * How long after the marker is written the next document still treats itself as the
 * same session. A reload is near-instant, so this only has to cover a slow cold start
 * on a weak tablet — while staying short enough that a marker left behind by an aborted
 * reload can not swallow the start of a genuine relaunch.
 */
const PAGE_REFRESH_TTL_MS = 30_000;

/** Called immediately before `window.location.reload()`. Fire-and-forget, never throws. */
export function markPageRefresh(): void {
  try {
    sessionStorage.setItem(PAGE_REFRESH_KEY, String(Date.now()));
  } catch {
    /* storage unavailable — the refresh is then simply reported as a normal open */
  }
}

/** True when this document was loaded by a marked refresh and has not consumed it yet. */
function hasFreshPageRefreshMarker(): boolean {
  try {
    const written = Number(sessionStorage.getItem(PAGE_REFRESH_KEY) || 0) || 0;
    if (!written) return false;
    const age = Date.now() - written;
    if (age < 0 || age > PAGE_REFRESH_TTL_MS) {
      // Stale (an aborted reload) or a clock jump — never trust it.
      sessionStorage.removeItem(PAGE_REFRESH_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * True while a marked refresh is in flight — used by the `pagehide` handler to tell a
 * reload apart from a real teardown. READ ONLY: the next document still needs it.
 */
export function isPageRefreshInFlight(): boolean {
  return hasFreshPageRefreshMarker();
}

/**
 * Reads the marker for THIS document and clears it — it is a one-shot signal, so a
 * later genuine close/open of the same document is tracked normally again.
 *
 * `false` means this is an ordinary launch and the start must be reported.
 */
export function consumePageRefreshMarker(): boolean {
  try {
    const fresh = hasFreshPageRefreshMarker();
    sessionStorage.removeItem(PAGE_REFRESH_KEY);
    return fresh;
  } catch {
    return false;
  }
}
